import { useSyncExternalStore } from "react";
import { useAuth } from "react-oidc-context";
import { apiPost } from "../../api";
import { holdTab, printShippingLabels } from "../../labelFlow";
import { useTicketContext } from "./useTicket";
import { confirmBox } from "../../confirm";

/**
 * What the label buttons are doing, shared by the Shipping Label card and the More Actions menu (each has its own
 * useLabels): `busy` is the step in progress, `pdfUrl` the last labels PDF for the card's "Download labels (PDF)" link.
 */
interface LabelState { ticketNo: number | null; busy: string | null; pdfUrl: string | null }
let state: LabelState = { ticketNo: null, busy: null, pdfUrl: null };
const listeners = new Set<() => void>();
function set(next: Partial<LabelState>) {
  if (next.pdfUrl !== undefined && state.pdfUrl && state.pdfUrl !== next.pdfUrl && state.pdfUrl.startsWith("blob:")) URL.revokeObjectURL(state.pdfUrl);
  state = { ...state, ...next };
  listeners.forEach((l) => l());
}
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

/** Print Shipping Labels and Replace Shipping Labels for the open ticket. */
export function useLabels() {
  const { config, detail, reload, notify } = useTicketContext();
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const no = detail.ticket.ticketNo;
  const s = useSyncExternalStore(subscribe, () => state);
  const mine = s.ticketNo === no;
  const busy = mine ? s.busy : null;
  const pdfUrl = mine ? s.pdfUrl : null;
  const setBusy = (b: string | null) => set({ ticketNo: no, busy: b });

  // The tab is opened on the click (one opened after waiting is blocked); the PDF is then fetched and shown in it,
  // and the card always offers "Download labels (PDF)" too (owner, 2026-11-04: the tab stayed "about:blank").
  const print = () => {
    if (busy) return;
    const tab = window.open("", "_blank");
    set({ ticketNo: no, busy: "Checking labels…", pdfUrl: null });
    notify(null);
    printShippingLabels(config, token, [no], tab, setBusy)
      .then((r) => { set({ pdfUrl: r.pdfUrl }); notify(r.text); })
      .catch((e: Error) => notify(`Labels: ${e.message}`))
      .finally(() => { setBusy(null); reload(); });
  };

  const replace = async () => {
    if (busy) return;
    if (!(await confirmBox(`Replace the shipping labels for #${no}?\n\nBoth current labels (to the customer and the spare return label) are voided and a refund is requested from Shippo, then new labels are bought and printed. Throw away the old printed labels.`, { title: "Replace labels", ok: "Replace labels", danger: true }))) return;
    const tab = window.open("", "_blank");
    holdTab(tab, "Replacing shipping labels…");
    set({ ticketNo: no, busy: "Replacing labels…", pdfUrl: null });
    notify(null);
    apiPost<{ refunds: string[]; problems: { ticketNo: number; error: string }[] }>(config, token, "/tickets/actions/replace-labels", { ticketNos: [no] })
      .then(async (r) => {
        if (r.problems.length) { tab?.close(); notify(`Old labels voided, but new ones could not be bought: ${r.problems.map((p) => p.error).join("; ")}`); return; }
        const p = await printShippingLabels(config, token, [no], tab, setBusy);
        set({ pdfUrl: p.pdfUrl });
        notify(`Old labels voided (refund ${r.refunds.join(", ").toLowerCase() || "not needed"}). ${p.text}`);
      })
      .catch((e: Error) => { tab?.close(); notify(`Labels: ${e.message}`); })
      .finally(() => { setBusy(null); reload(); });
  };

  return { busy, pdfUrl, print, replace };
}
